import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  CheckCircle2,
  CloudOff,
  Scale,
  RotateCcw,
} from "lucide-react";
import {
  Link,
  useNavigate,
} from "react-router-dom";
import {
  Card,
  InlineNotice,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { db } from "../lib/db";
import { useAuth } from "../lib/auth";
import { refreshFromServer } from "../lib/data";
import { todayBusinessDate } from "../lib/format";

interface Closing {
  id: string;
  businessDate: string;
  systemCash: number;
  actualCash: number;
  discrepancy: number;
  reason?: string | null;
  createdAt: string;
  state?: "OPEN" | "CLOSED" | "REOPENED";
}

export function DailyClosingPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const dashboard = useLiveQuery(
    async () =>
      (await db.meta.get("dashboard"))
        ?.value as any,
    [],
  );

  const pendingCommands =
    useLiveQuery(
      () =>
        db.syncQueue
          .where("status")
          .anyOf(
            "PENDING",
            "FAILED",
            "SYNCING",
          )
          .count(),
      [],
      0,
    );

  const systemCash =
    dashboard?.accounts?.find(
      (account: any) =>
        account.type === "CASH",
    )?.balance ?? 0;

  const [actualCash, setActualCash] =
    useState(0);
  const [actualTouched, setActualTouched] =
    useState(false);
  const [reason, setReason] =
    useState("");
  const [latest, setLatest] =
    useState<Closing | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const businessDate =
    todayBusinessDate();
  const onlineRequired =
    !auth.demo && !navigator.onLine;

  useEffect(() => {
    if (!actualTouched) {
      setActualCash(systemCash);
    }
  }, [systemCash, actualTouched]);

  useEffect(() => {
    if (
      auth.demo ||
      !navigator.onLine
    ) {
      return;
    }

    void api<Closing | null>(
      "/closing/latest",
    )
      .then((closing) => {
        if (!closing) {
          setLatest(null);
          return;
        }

        setLatest({
          ...closing,
          systemCash: Number(
            closing.systemCash,
          ),
          actualCash: Number(
            closing.actualCash,
          ),
          discrepancy: Number(
            closing.discrepancy,
          ),
          businessDate:
            String(
              closing.businessDate,
            ).slice(0, 10),
        });
      })
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : "Không thể kiểm tra trạng thái đóng sổ",
        ),
      );
  }, [auth.demo, businessDate]);

  const closedToday =
    latest?.businessDate ===
      businessDate &&
    latest?.state !== "REOPENED";

  const discrepancy = useMemo(
    () => actualCash - systemCash,
    [actualCash, systemCash],
  );

  async function synchronizeFirst() {
    if (
      pendingCommands <= 0 ||
      auth.demo
    ) {
      return true;
    }

    setError(
      "Cần đồng bộ toàn bộ giao dịch đang chờ trước khi đóng sổ.",
    );
    return false;
  }

  async function reopenDay() {
    setError("");

    if (
      auth.session?.user.role !==
      "OWNER"
    ) {
      setError(
        "Chỉ Chủ cửa hàng được mở lại ngày đã đóng.",
      );
      return;
    }

    if (!navigator.onLine) {
      setError(
        "Mở lại ngày cần kết nối mạng.",
      );
      return;
    }

    if (
      reopenReason.trim().length < 3
    ) {
      setError(
        "Cần nhập lý do mở lại ngày.",
      );
      return;
    }

    setReopenBusy(true);

    try {
      await api(
        "/closing/reopen",
        {
          method: "POST",
          body: JSON.stringify({
            idempotencyKey:
              crypto.randomUUID(),
            businessDate,
            reason:
              reopenReason.trim(),
          }),
        },
      );

      if (latest) {
        const reopened = {
          ...latest,
          state: "REOPENED" as const,
        };
        setLatest(reopened);
        await db.meta.put({
          key: "last-closing",
          value: reopened,
        });
      }

      setReopenReason("");
      setActualTouched(false);
      await refreshFromServer();
    } catch (err) {
      if (
        (err as { status?: number })
          .status === 401
      ) {
        auth.invalidateSession();
        return;
      }

      setError(
        err instanceof Error
          ? err.message
          : "Không thể mở lại ngày",
      );
    } finally {
      setReopenBusy(false);
    }
  }

  async function submit() {
    setError("");

    if (closedToday) {
      setError(
        "Ngày hôm nay đã được đóng sổ.",
      );
      return;
    }

    if (onlineRequired) {
      setError(
        "Đóng sổ cần kết nối mạng để đối chiếu số dư authoritative.",
      );
      return;
    }

    if (
      !(await synchronizeFirst())
    ) {
      return;
    }

    if (
      discrepancy !== 0 &&
      !reason.trim()
    ) {
      setError(
        "Cần chọn lý do chênh lệch.",
      );
      return;
    }

    setBusy(true);

    try {
      if (auth.demo) {
        await db.meta.put({
          key: "last-closing",
          value: {
            id: crypto.randomUUID(),
            businessDate,
            systemCash,
            actualCash,
            discrepancy,
            reason:
              reason.trim() || null,
            createdAt:
              new Date().toISOString(),
            state: "CLOSED",
          },
        });

        const record =
          await db.meta.get(
            "dashboard",
          );

        if (record?.value) {
          const next =
            structuredClone(
              record.value,
            ) as any;
          const cash =
            next.accounts?.find(
              (account: any) =>
                account.type ===
                "CASH",
            );
          if (cash) {
            cash.balance =
              actualCash;
          }
          await db.meta.put({
            key: "dashboard",
            value: next,
          });
        }
      } else {
        const result =
          await api<Closing>(
            "/closing",
            {
              method: "POST",
              body: JSON.stringify({
                idempotencyKey:
                  crypto.randomUUID(),
                businessDate,
                actualCash:
                  Math.round(
                    actualCash,
                  ),
                reason:
                  reason.trim() ||
                  undefined,
              }),
            },
          );

        await db.meta.put({
          key: "last-closing",
          value: result,
        });

        await refreshFromServer();
      }

      navigate("/", {
        replace: true,
      });
    } catch (err) {
      const status = (
        err as { status?: number }
      ).status;

      if (status === 401) {
        auth.invalidateSession();
        return;
      }

      setError(
        err instanceof Error
          ? err.message
          : "Không thể đóng sổ ngày",
      );
    } finally {
      setBusy(false);
    }
  }

  if (closedToday && latest) {
    return (
      <div className="page-stack">
        <PageHeader
          back
          title="Đóng sổ ngày"
          description={businessDate}
        />

        <InlineNotice
          tone="success"
          title="Ngày hôm nay đã đóng sổ"
        >
          Sổ tiền đã được đối chiếu. Nếu có
          chênh lệch, hệ thống đã tạo movement
          điều chỉnh thay vì sửa mất lịch sử.
        </InlineNotice>

        <Card className="closing-card">
          <div className="closing-row">
            <span>
              Tiền hệ thống trước đóng
            </span>
            <Money
              value={latest.systemCash}
            />
          </div>
          <div className="closing-row">
            <span>Tiền thực tế</span>
            <Money
              value={latest.actualCash}
            />
          </div>
          <div className="closing-row">
            <span>Chênh lệch</span>
            <Money
              value={latest.discrepancy}
              className={
                latest.discrepancy === 0
                  ? "text-success"
                  : "text-danger"
              }
            />
          </div>
          {latest.reason ? (
            <div className="closing-row">
              <span>Lý do</span>
              <strong>
                {latest.reason}
              </strong>
            </div>
          ) : null}
        </Card>

        {auth.session?.user.role === "OWNER" ? (
          <Card className="form-stack">
            <InlineNotice
              tone="warning"
              title="Mở lại ngày là thao tác nhạy cảm"
            >
              Sau khi mở lại, hệ thống cho phép ghi thêm giao dịch trong ngày.
              Khi hoàn tất, cần đóng sổ lại. Toàn bộ thao tác được lưu lịch sử.
            </InlineNotice>

            <label>
              Lý do mở lại
              <input
                value={reopenReason}
                onChange={(event) =>
                  setReopenReason(
                    event.target.value,
                  )
                }
                placeholder="Ví dụ: còn giao dịch phát sinh chưa ghi nhận"
              />
            </label>

            <button
              type="button"
              className="btn btn-secondary btn-lg"
              disabled={
                reopenBusy ||
                !navigator.onLine ||
                reopenReason.trim().length < 3
              }
              onClick={() =>
                void reopenDay()
              }
            >
              <RotateCcw size={20} />
              {reopenBusy
                ? "Đang mở lại..."
                : "Mở lại ngày"}
            </button>
          </Card>
        ) : (
          <InlineNotice
            tone="info"
            title="Ngày đã khóa giao dịch"
          >
            Nếu cần ghi bổ sung, Chủ cửa hàng phải mở lại ngày và hệ thống sẽ lưu audit.
          </InlineNotice>
        )}

        <Link
          to="/activity"
          className="btn btn-secondary btn-lg"
        >
          Xem lịch sử hoạt động
        </Link>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Đóng sổ ngày"
        description={businessDate}
      />

      {error ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      {onlineRequired ? (
        <InlineNotice
          tone="warning"
          title="Đóng sổ cần online"
        >
          <span>
            <CloudOff size={16} /> Các giao
            dịch bán vẫn có thể làm offline,
            nhưng đóng sổ cần số dư
            authoritative để tránh lệch giữa
            nhiều thiết bị.
          </span>
        </InlineNotice>
      ) : null}

      {pendingCommands > 0 ? (
        <InlineNotice
          tone="warning"
          title="Còn giao dịch chưa đồng bộ"
        >
          <span>
            <StatusBadge tone="warning">
              {pendingCommands}
            </StatusBadge>{" "}
            giao dịch cần đồng bộ trước khi
            đóng sổ.
          </span>
        </InlineNotice>
      ) : null}

      <InlineNotice
        tone="info"
        title="Đối chiếu trước khi đóng"
      >
        Đếm tiền thực tế trong két. Nếu bị
        lệch, lý do là bắt buộc và hệ thống sẽ
        tạo một movement điều chỉnh để số dư
        ngày sau bắt đầu đúng.
      </InlineNotice>

      <Card className="closing-card">
        <div className="closing-row">
          <span>Doanh thu thuần hôm nay</span>
          <Money
            value={
              dashboard?.revenue ?? 0
            }
          />
        </div>

        <div className="closing-row">
          <span>Tiền mặt hệ thống</span>
          <Money value={systemCash} />
        </div>

        <label className="closing-input">
          Tiền mặt thực tế
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={actualCash}
            onChange={(event) => {
              setActualTouched(true);
              setActualCash(
                Number(
                  event.target.value,
                ) || 0,
              );
            }}
          />
        </label>

        <div className="closing-difference">
          <Scale size={22} />
          <div>
            <span>Chênh lệch</span>
            <Money
              value={discrepancy}
              className={
                discrepancy === 0
                  ? "text-success"
                  : "text-danger"
              }
            />
          </div>
        </div>

        {discrepancy !== 0 ? (
          <label>
            Lý do chênh lệch
            <select
              value={reason}
              onChange={(event) =>
                setReason(
                  event.target.value,
                )
              }
            >
              <option value="">
                Chọn lý do
              </option>
              <option>
                Thối nhầm tiền cho khách
              </option>
              <option>
                Thiếu chứng từ chi
              </option>
              <option>
                Thu/chi chưa ghi sổ
              </option>
              <option>
                Kiểm đếm thực tế khác hệ thống
              </option>
              <option>Khác</option>
            </select>
          </label>
        ) : (
          <InlineNotice
            tone="success"
            title="Tiền mặt đã khớp"
          />
        )}
      </Card>

      <button
        className="btn btn-primary btn-lg sticky-action"
        disabled={
          busy ||
          onlineRequired ||
          pendingCommands > 0 ||
          (discrepancy !== 0 &&
            !reason)
        }
        onClick={() => void submit()}
      >
        <CheckCircle2 size={20} />{" "}
        {busy
          ? "Đang đóng sổ..."
          : "Xác nhận đóng sổ ngày"}
      </button>
    </div>
  );
}
